import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { ProductTierSku, Role } from '@vendia/management-api-types'
import clsx from 'clsx'
import { debug } from 'debug'
import { useEffect, useRef } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { useLocalstorageState } from 'rooks'
import Icon from 'src/components/icons/icon'
import UserIcon from 'src/components/icons/user-icon'
import { refreshSession } from 'src/utils/auth/cognito'
import useGetCurrentVendiaUserQuery from 'src/utils/hooks/use-current-vendia-user-query'
import useFeatureToggle, { Features } from 'src/utils/hooks/use-feature-toggle'
import { useGetOrg } from 'src/utils/hooks/use-get-org'
import notify from 'src/utils/notify'
import { selectedRoleState, showQueryDevToolsState } from 'src/utils/state'
import { getTierDisplayName, Tier } from 'src/utils/subscription'

const logger = debug('app:UserDropdown')

export function UserDropdown() {
  const { isEnabled } = useFeatureToggle(Features.DEV_SETTINGS)
  const [showQueryDevTools, toggleShowQueryDevTools] = useRecoilState(showQueryDevToolsState)
  const { getCurrentVendiaUserQuery } = useGetCurrentVendiaUserQuery()
  const getOrg = useGetOrg()
  const user = getCurrentVendiaUserQuery?.data?.getUser
  const subscriptionTier = getOrg?.data?.getOrganization?.subscription?.tier as Tier
  const productTierSku = getOrg?.data?.getOrganization?.productTier?.sku
  const location = useLocation()
  const { id: uniName, node: nodeName } = useParams()

  // Extract node from pathname if not in params (e.g., /uni/:id/:node/settings)
  const pathParts = location.pathname.split('/').filter(Boolean)
  const nodeFromPath = pathParts[2] // /uni/:id/:node
  const effectiveNodeName = nodeName || nodeFromPath

  // Check if we're on a uni page and user is Individual or Pro tier
  const isOnUniPage = location.pathname.startsWith('/uni/') && uniName && effectiveNodeName
  const isIndividualOrPro =
    productTierSku === ProductTierSku.Individual || productTierSku === ProductTierSku.Pro
  const showUniSettingsLink = isOnUniPage && isIndividualOrPro
  const uniSettingsPath = isOnUniPage ? `/uni/${uniName}/${effectiveNodeName}/settings` : null

  const lastNotificationRef = useRef<any>(null)
  const [selectedRole, setSelectedRole] = useRecoilState(selectedRoleState)
  const roles = user?.roles as Role[] | undefined

  const [isDarkMode, setIsDarkMode] = useLocalstorageState('vendia-dev-dark-mode', false)
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode)
    document.documentElement.classList.toggle('vendia-dev-dark-mode')
  }
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('vendia-dev-dark-mode')
    }
  }, [isDarkMode])

  async function handleSelectRole(role: Role) {
    logger('handleSelectRole', role.name)
    // Set role in local storage so it persists across page refreshes
    localStorage.setItem(`selectedRoleName:${user?.userId}`, role.name)

    // A tiny bit of delay to make extra sure item is set in local storage before we switch roles in app state
    // State update will trigger refetch of queries with selected role in query key and use-api will grab selected
    //  role from local storage - see use use-api.js for more details,
    await new Promise((resolve) => setTimeout(resolve, 150))
    setSelectedRole({ name: role.name })

    // Note: whether using useRef or useState, this dismissal doesn't work the first time for whatever reason but it's just a minor UX improvement
    // so not spending time to figure out the problem (just feels nicer to dismiss last toast rather than stack them when switching roles quickly)
    notify.dismiss(lastNotificationRef.current)
    await new Promise((resolve) => setTimeout(resolve, 500))
    lastNotificationRef.current = notify.success(`All actions will now be performed using your ${role.name} role`)

    await refreshSession()
  }

  const menuItemClasses = 'cursor-pointer w-full block px-6 py-3 text-xs font-bold data-[focus]:bg-tintLight'

  return (
    <div data-testid='user-dropdown'>
      <Menu as='div' className='relative inline-block text-left'>
        <div>
          <MenuButton disabled={user?.email === undefined} data-testid='user-nav'>
            <UserIcon user={user!} isInteractive />
          </MenuButton>
        </div>
        <MenuItems
          className='ring-outline absolute -right-0 z-30 mt-2 min-w-[360px] origin-top-right overflow-hidden rounded-lg bg-slate-100 shadow-2xl ring ring-1 transition duration-200 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0'
          transition
        >
          <div className='flex justify-between gap-8 rounded-t-md border-b p-6'>
            <div className='flex items-center gap-4'>
              <UserIcon user={user!} ignoreTheme />
              <div className='max-w-xs text-xs'>
                {user?.firstName && (
                  <div className='truncate font-bold'>
                    {user?.firstName} {user?.lastName}
                  </div>
                )}
                <div className='truncate'>{user?.email}</div>
                <div className='flex items-center overflow-hidden whitespace-nowrap text-xs uppercase text-gray-500'>
                  {getTierDisplayName(subscriptionTier, productTierSku)}
                </div>
              </div>
            </div>
          </div>
          <div className='flex max-h-[600px] flex-col bg-white'>
            <div className='overflow-y-auto pt-2'>
              <MenuItem>
                <Link to='/org/overview' className={menuItemClasses}>
                  <div>My Organization</div>
                </Link>
              </MenuItem>
              {showUniSettingsLink && uniSettingsPath && (
                <MenuItem>
                  <Link to={uniSettingsPath} className={menuItemClasses}>
                    <div>Project Settings</div>
                  </Link>
                </MenuItem>
              )}
              <MenuItem>
                <Link to='/settings' className={menuItemClasses}>
                  <div>Account Settings</div>
                </Link>
              </MenuItem>
              <MenuItem>
                <a
                  href='https://docs.vendia.com/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className={menuItemClasses}
                >
                  <div>
                    <div className='flex items-center'>
                      <span>Documentation</span>
                      <Icon size={'xs'} className='ml-2' name='external' />
                    </div>
                  </div>
                </a>
              </MenuItem>
              {isEnabled && (
                <>
                  <MenuItem>
                    <Link to='/developer' className={menuItemClasses}>
                      Internal Developer Settings
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <span
                      className={menuItemClasses}
                      onClick={() => toggleShowQueryDevTools((prev) => !prev)}
                    >{`${showQueryDevTools ? 'Hide' : 'Show'} Query Dev Tools`}</span>
                  </MenuItem>
                  <MenuItem>
                    <button className={clsx(menuItemClasses, 'text-left')} onClick={toggleDarkMode}>
                      Toggle Dark Mode
                    </button>
                  </MenuItem>
                </>
              )}
              {roles && roles?.length > 1 ? (
                <div className='mt-2 border-t py-2'>
                  <div className='text-light flex bg-white px-6 py-2 text-xs'>Role Selection</div>
                  <div className='flex flex-col bg-white'>
                    {roles?.map((role) => {
                      const selected = selectedRole?.name === role.name
                      return (
                        <MenuItem key={role.name}>
                          <button
                            className={`data-[focus]:bg-tintLight flex w-full items-center justify-between gap-2 px-6 py-3 ${selected && 'bg-tintLight'}`}
                            onClick={() => handleSelectRole(role)}
                          >
                            <div className='flex max-w-md items-baseline truncate text-xs font-bold'>{role.name}</div>
                            <div className='mr-2 h-5'>
                              {selected ? (
                                <Icon name='check' size={16} stroke='#6800EB' data-testid='selected-role-check' />
                              ) : null}
                            </div>
                          </button>
                        </MenuItem>
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </div>
            <MenuItem>
              <Link to='/logout' className={`${menuItemClasses} rounded-b-md border-t bg-slate-100 py-5`}>
                <div>Sign out</div>
              </Link>
            </MenuItem>
          </div>
        </MenuItems>
      </Menu>
    </div>
  )
}
